import { Form, useLocation } from "@remix-run/react";
import { Check, ChevronDown, Languages } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "#app/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu";
import { type AppLocale, supportedLngs } from "#app/i18n/i18n";
import { cn } from "#app/utils/misc";

const LOCALE_LABELS: Record<AppLocale, string> = {
  en: "English",
  ja: "日本語",
};

export function LocaleSwitcher({
  className,
  variant = "default",
}: {
  className?: string;
  variant?: "default" | "landing" | "icon" | "settings";
}) {
  const { i18n, t } = useTranslation("common");
  const location = useLocation();
  const current = (i18n.language?.split("-")[0] ?? "en") as AppLocale;
  const formRef = useRef<HTMLFormElement>(null);
  const localeInputRef = useRef<HTMLInputElement>(null);

  const selectLocale = (locale: AppLocale) => {
    if (locale === current || !formRef.current || !localeInputRef.current) {
      return;
    }

    localeInputRef.current.value = locale;
    formRef.current.requestSubmit();
  };

  return (
    <>
      <Form
        ref={formRef}
        method="post"
        action="/resources/set-locale"
        reloadDocument
        className="hidden"
        aria-hidden
      >
        <input ref={localeInputRef} type="hidden" name="locale" />
        <input
          type="hidden"
          name="redirectTo"
          value={`${location.pathname}${location.search}`}
        />
      </Form>

      {/* Base UI's menu is non-modal and does not put pointer-events:none on
          body, so the antd-settings-modal workaround Radix needed is gone.
          Direction is inherited from the document rather than a `dir` prop. */}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size={variant === "icon" ? "icon" : "sm"}
              className={cn(
                "group h-9 max-w-full gap-2 rounded-full border border-border bg-background px-3 text-sm font-medium text-foreground transition-[background-color,border-color,color,box-shadow] hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
                variant === "landing" && "bg-background/90 backdrop-blur-sm",
                variant === "icon" && "h-9 w-9 min-w-9 p-0",
                variant === "settings" &&
                  "w-full max-w-72 justify-between sm:w-72",
                className,
              )}
              aria-label={`${t("language")}: ${LOCALE_LABELS[current] ?? current}`}
              title={variant === "icon" ? t("language") : undefined}
            />
          }
        >
          <Languages className="h-4 w-4 shrink-0" aria-hidden />
          {variant === "icon" ? null : (
            <>
              <span className="min-w-0 truncate" dir="auto">
                {LOCALE_LABELS[current] ?? current}
              </span>
              <ChevronDown
                className="ms-1 h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180"
                aria-hidden
              />
            </>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className={cn(
            "w-(--anchor-width) min-w-48 max-w-[calc(100vw-2rem)] overflow-hidden space-y-1",
            // antd Modal renders at z-index 1000, so the default z-50 hides this.
            variant === "settings" && "z-1100",
          )}
        >
          {supportedLngs.map((locale) => {
            const selected = current === locale;
            return (
              <DropdownMenuItem
                key={locale}
                role="menuitemradio"
                aria-label={LOCALE_LABELS[locale]}
                aria-checked={selected}
                // Base UI Menu.Item uses `onClick`, not Radix's `onSelect`.
                onClick={() => {
                  selectLocale(locale);
                }}
                className={cn(
                  "min-h-9 w-full cursor-pointer gap-3 px-2.5 py-2 text-start",
                  selected && "bg-accent font-semibold text-accent-foreground",
                )}
              >
                <span className="min-w-0 flex-1 truncate" dir="auto">
                  {LOCALE_LABELS[locale]}
                </span>
                <span className="ms-auto flex h-4 w-4 shrink-0 items-center justify-center">
                  {selected ? <Check className="h-4 w-4" aria-hidden /> : null}
                </span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
